import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  DAExtensionState,
  TermCandidateWorkflow,
  WordCandidateWorkflow,
  NamingRuleWorkflow,
  EntityRefineWorkflow,
  AttributeRefineWorkflow,
  QualityInspectionWorkflow,
  QualityApplyWorkflow,
  StandardAnalysisWorkflow,
  AttributeBatchWorkflow,
  LLMSettings,
  RefineResult,
  WorkflowStepBase,
} from '../../types';

/**
 * DA Extension Redux Slice
 *
 * 표준조사(Standard Survey) 및 모델정제(Model Refinement) 기능 관련 상태 관리
 * - 용어 후보 조사 (Term Candidate Workflow): 5단계
 * - 단어 후보 조사 (Word Candidate Workflow): 2단계
 * - 명명규칙 조사 (Naming Rule Workflow): 3단계
 * - 엔터티명 정제 (Entity Refine Workflow): 4단계
 * - 속성명 정제 (Attribute Refine Workflow): 4단계
 */

// ============================================================
// Payload Types
// ============================================================

/** 모델 키 기본 페이로드 */
interface ModelKeyPayload {
  modelKey: string;
}

/** 워크플로우 스텝 설정 페이로드 */
interface SetWorkflowStepPayload {
  modelKey: string;
  result: Record<string, unknown>;
}

/** 워크플로우 현재 스텝 페이로드 */
interface SetCurrentStepPayload {
  modelKey: string;
  step: number;
}

/** 명명규칙 스텝1 페이로드 */
interface SetNamingRuleStep1Payload {
  modelKey: string;
  result: {
    namingRules?: {
      entityLogicalName?: unknown[];
      entityPhysicalName?: unknown[];
      attributeLogicalName?: unknown[];
      attributePhysicalName?: unknown[];
    };
    [key: string]: unknown;
  };
  append?: boolean;
}

/** LLM 설정 페이로드 */
interface SetLLMSettingsPayload {
  modelKey: string;
  settings: LLMSettings;
}

/** 정제규칙 페이로드 */
interface RefinementMethodPayload {
  modelKey: string;
  method: { id: string; [key: string]: unknown };
}

/** 정제규칙 제거 페이로드 */
interface RemoveRefinementMethodPayload {
  modelKey: string;
  methodId: string;
}

/** 제안 수락/거절 페이로드 */
interface SuggestionPayload {
  modelKey: string;
  suggestionId: string;
}

/** 스텝 초기화 페이로드 */
interface ResetStepsPayload {
  modelKey: string;
  resetSteps?: number[];
}

/** 워크플로우 저장 설정 페이로드 */
interface SetPersistWorkflowPayload {
  modelKey: string;
  persist: boolean;
}

/** 사전 등록 페이로드 */
interface RegisterToDictionaryPayload {
  modelKey: string;
  terms?: Array<{
    logicalNameKr?: string;
    logicalName?: string;
    physicalName?: string;
    [key: string]: unknown;
  }>;
  words?: Array<{
    logicalWordKr?: string;
    logicalName?: string;
    physicalWord?: string;
    physicalName?: string;
    [key: string]: unknown;
  }>;
  domains?: Array<{
    name?: string;
    domainName?: string;
    domainAbbr?: string;
    dataType?: string;
    length?: number;
    precision?: number;
    [key: string]: unknown;
  }>;
  registeredFrom?: string;
}

/** 사전 항목 제거 페이로드 */
interface RemoveFromDictionaryPayload {
  modelKey: string;
  dictionaryType: 'term' | 'word' | 'domain';
  itemId?: string;
  logicalName?: string;
  physicalName?: string;
  name?: string;
}

/** 사전 항목 수정 페이로드 */
interface UpdateDictionaryItemPayload {
  modelKey: string;
  originalPhysicalName?: string;
  originalName?: string;
  updatedTerm?: Record<string, unknown>;
  updatedWord?: Record<string, unknown>;
  updatedDomain?: Record<string, unknown>;
  updatedFrom?: string;
}

// ============================================================
// Initial State
// ============================================================

export const initialState: DAExtensionState = {
  // 표준조사 결과 (Standard Survey Results)
  namingRuleResult: null,
  wordCandidateResult: null,
  termCandidateResult: null,
  domainCandidateResult: null,
  projectTermCandidateResult: null,

  // 워크플로우
  termCandidateWorkflow: {},
  wordCandidateWorkflow: {},
  namingRuleWorkflow: {},
  entityRefineWorkflow: {},
  attributeRefineWorkflow: {},
  qualityInspectionWorkflow: {},
  qualityApplyWorkflow: {},
  standardAnalysisWorkflow: {},
  attributeBatchWorkflow: {},

  // 모델정제 결과 (하위 호환성 유지)
  entityRefineResult: null,
  attributeRefineResult: null,
};

// ============================================================
// Slice
// ============================================================

export const daExtensionSlice = createSlice({
  name: 'base/daExtension',
  initialState,

  reducers: {
    // ========================================
    // 표준조사 결과 액션
    // ========================================

    setNamingRuleResult: (state, action: PayloadAction<unknown>) => {
      state.namingRuleResult = action.payload;
    },

    setWordCandidateResult: (state, action: PayloadAction<unknown>) => {
      state.wordCandidateResult = action.payload;
    },

    setTermCandidateResult: (state, action: PayloadAction<unknown>) => {
      state.termCandidateResult = action.payload;
    },

    setDomainCandidateResult: (state, action: PayloadAction<unknown>) => {
      state.domainCandidateResult = action.payload;
    },

    setProjectTermCandidateResult: (state, action: PayloadAction<unknown>) => {
      state.projectTermCandidateResult = action.payload;
    },

    clearProjectTermCandidateResult: (state) => {
      state.projectTermCandidateResult = null;
    },

    // ========================================
    // 용어 후보 조사 5단계 워크플로우 액션들
    // ========================================

    initTermCandidateWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      state.termCandidateWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        step5: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setTermWorkflowStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 } as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 } as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 } as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 } as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].step4 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowStep5: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = { currentStep: 1 } as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].step5 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setTermWorkflowCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.termCandidateWorkflow) {
        state.termCandidateWorkflow = {};
      }
      if (!state.termCandidateWorkflow[modelKey]) {
        state.termCandidateWorkflow[modelKey] = {} as TermCandidateWorkflow;
      }
      state.termCandidateWorkflow[modelKey].currentStep = step;
      state.termCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearTermCandidateWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.termCandidateWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.termCandidateWorkflow[modelKey];
      } else {
        state.termCandidateWorkflow = {};
      }
    },

    // ========================================
    // 단어 후보 조사 2단계 워크플로우 액션들
    // ========================================

    initWordCandidateWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      state.wordCandidateWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setWordWorkflowStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      if (!state.wordCandidateWorkflow[modelKey]) {
        state.wordCandidateWorkflow[modelKey] = { currentStep: 1 } as WordCandidateWorkflow;
      }
      state.wordCandidateWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.wordCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setWordWorkflowStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      if (!state.wordCandidateWorkflow[modelKey]) {
        state.wordCandidateWorkflow[modelKey] = { currentStep: 1 } as WordCandidateWorkflow;
      }
      state.wordCandidateWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.wordCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setWordWorkflowStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      if (!state.wordCandidateWorkflow[modelKey]) {
        state.wordCandidateWorkflow[modelKey] = { currentStep: 1 } as WordCandidateWorkflow;
      }
      state.wordCandidateWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.wordCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setWordWorkflowStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      if (!state.wordCandidateWorkflow[modelKey]) {
        state.wordCandidateWorkflow[modelKey] = { currentStep: 1 } as WordCandidateWorkflow;
      }
      (state.wordCandidateWorkflow[modelKey] as Record<string, unknown>).step4 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.wordCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setWordWorkflowCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.wordCandidateWorkflow) {
        state.wordCandidateWorkflow = {};
      }
      if (!state.wordCandidateWorkflow[modelKey]) {
        state.wordCandidateWorkflow[modelKey] = {} as WordCandidateWorkflow;
      }
      state.wordCandidateWorkflow[modelKey].currentStep = step;
      state.wordCandidateWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearWordCandidateWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.wordCandidateWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.wordCandidateWorkflow[modelKey];
      } else {
        state.wordCandidateWorkflow = {};
      }
    },

    // ========================================
    // 명명규칙 조사 3단계 워크플로우 액션들
    // ========================================

    initNamingRuleWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.namingRuleWorkflow) {
        state.namingRuleWorkflow = {};
      }
      state.namingRuleWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setNamingRuleWorkflowStep1: (state, action: PayloadAction<SetNamingRuleStep1Payload>) => {
      const { modelKey, result, append = false } = action.payload;
      if (!state.namingRuleWorkflow) {
        state.namingRuleWorkflow = {};
      }
      if (!state.namingRuleWorkflow[modelKey]) {
        state.namingRuleWorkflow[modelKey] = { currentStep: 1 } as NamingRuleWorkflow;
      }

      // append=true면 기존 규칙에 추가
      const existingStep1 = state.namingRuleWorkflow[modelKey].step1 as
        | { namingRules?: Record<string, unknown[]> }
        | undefined;
      if (append && existingStep1?.namingRules) {
        const existingRules = existingStep1.namingRules;
        const newRules = result.namingRules || {};

        // 각 카테고리별로 병합
        const mergedRules: Record<string, unknown[]> = {};
        (
          [
            'entityLogicalName',
            'entityPhysicalName',
            'attributeLogicalName',
            'attributePhysicalName',
          ] as const
        ).forEach((category) => {
          const existing = (existingRules[category] as unknown[]) || [];
          const incoming = (newRules[category] as unknown[]) || [];
          // 중복 제거하면서 병합
          const merged = [...existing];
          incoming.forEach((rule) => {
            const r = rule as { pattern?: string; regex?: string };
            const isDuplicate = merged.some((m) => {
              const mr = m as { pattern?: string; regex?: string };
              return mr.pattern === r.pattern && mr.regex === r.regex;
            });
            if (!isDuplicate) {
              merged.push(rule);
            }
          });
          mergedRules[category] = merged;
        });

        state.namingRuleWorkflow[modelKey].step1 = {
          ...existingStep1,
          namingRules: mergedRules,
          timestamp: new Date().toISOString(),
        };
      } else {
        state.namingRuleWorkflow[modelKey].step1 = {
          ...result,
          timestamp: new Date().toISOString(),
        };
      }
      state.namingRuleWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setNamingRuleWorkflowStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.namingRuleWorkflow) {
        state.namingRuleWorkflow = {};
      }
      if (!state.namingRuleWorkflow[modelKey]) {
        state.namingRuleWorkflow[modelKey] = { currentStep: 1 } as NamingRuleWorkflow;
      }
      state.namingRuleWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.namingRuleWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setNamingRuleWorkflowStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.namingRuleWorkflow) {
        state.namingRuleWorkflow = {};
      }
      if (!state.namingRuleWorkflow[modelKey]) {
        state.namingRuleWorkflow[modelKey] = { currentStep: 1 } as NamingRuleWorkflow;
      }
      state.namingRuleWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.namingRuleWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setNamingRuleWorkflowCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.namingRuleWorkflow) {
        state.namingRuleWorkflow = {};
      }
      if (!state.namingRuleWorkflow[modelKey]) {
        state.namingRuleWorkflow[modelKey] = {} as NamingRuleWorkflow;
      }
      state.namingRuleWorkflow[modelKey].currentStep = step;
      state.namingRuleWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearNamingRuleWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.namingRuleWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.namingRuleWorkflow[modelKey];
      } else {
        state.namingRuleWorkflow = {};
      }
    },

    // ========================================
    // 모델정제 관련 액션
    // ========================================

    setEntityRefineResult: (state, action: PayloadAction<RefineResult | null>) => {
      state.entityRefineResult = action.payload;
    },

    setAttributeRefineResult: (state, action: PayloadAction<RefineResult | null>) => {
      state.attributeRefineResult = action.payload;
    },

    clearEntityRefineResult: (state) => {
      state.entityRefineResult = null;
    },

    clearAttributeRefineResult: (state) => {
      state.attributeRefineResult = null;
    },

    // ========================================
    // 엔터티명 정제 워크플로우 액션
    // ========================================

    initEntityRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      state.entityRefineWorkflow[modelKey] = {
        currentStep: 0,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        llmSettings: { model: '', skipCache: false },
        lastUpdated: new Date().toISOString(),
      };
    },

    setEntityRefineStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].step1 = result as WorkflowStepBase;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep,
        0
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].step2 = result as WorkflowStepBase;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep,
        1
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].step3 = result as WorkflowStepBase;
      state.entityRefineWorkflow[modelKey].currentStep = Math.max(
        state.entityRefineWorkflow[modelKey].currentStep,
        2
      );
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].step4 = result as WorkflowStepBase;
      state.entityRefineWorkflow[modelKey].currentStep = 3;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].currentStep = step;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setEntityRefineLLMSettings: (state, action: PayloadAction<SetLLMSettingsPayload>) => {
      const { modelKey, settings } = action.payload;
      if (!state.entityRefineWorkflow) {
        state.entityRefineWorkflow = {};
      }
      if (!state.entityRefineWorkflow[modelKey]) {
        state.entityRefineWorkflow[modelKey] = { currentStep: 0 } as EntityRefineWorkflow;
      }
      state.entityRefineWorkflow[modelKey].llmSettings = settings;
      state.entityRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearEntityRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload | undefined>) => {
      const modelKey = action?.payload?.modelKey;
      if (modelKey) {
        delete state.entityRefineWorkflow[modelKey];
      } else {
        state.entityRefineWorkflow = {};
      }
    },

    // ========================================
    // 속성명 정제 워크플로우 액션
    // ========================================

    initAttributeRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      state.attributeRefineWorkflow[modelKey] = {
        currentStep: 0,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        llmSettings: { model: '', skipCache: false },
        lastUpdated: new Date().toISOString(),
      };
    },

    setAttributeRefineStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].step1 = result as WorkflowStepBase;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep,
        0
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].step2 = result as WorkflowStepBase;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep,
        1
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].step3 = result as WorkflowStepBase;
      state.attributeRefineWorkflow[modelKey].currentStep = Math.max(
        state.attributeRefineWorkflow[modelKey].currentStep,
        2
      );
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].step4 = result as WorkflowStepBase;
      state.attributeRefineWorkflow[modelKey].currentStep = 3;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].currentStep = step;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeRefineLLMSettings: (state, action: PayloadAction<SetLLMSettingsPayload>) => {
      const { modelKey, settings } = action.payload;
      if (!state.attributeRefineWorkflow) {
        state.attributeRefineWorkflow = {};
      }
      if (!state.attributeRefineWorkflow[modelKey]) {
        state.attributeRefineWorkflow[modelKey] = { currentStep: 0 } as AttributeRefineWorkflow;
      }
      state.attributeRefineWorkflow[modelKey].llmSettings = settings;
      state.attributeRefineWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearAttributeRefineWorkflow: (state, action: PayloadAction<ModelKeyPayload | undefined>) => {
      const modelKey = action?.payload?.modelKey;
      if (modelKey) {
        delete state.attributeRefineWorkflow[modelKey];
      } else {
        state.attributeRefineWorkflow = {};
      }
    },

    // ========================================
    // 품질조사 3단계 워크플로우 액션들
    // ========================================

    initQualityInspectionWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      state.qualityInspectionWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setQualityWorkflowStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = { currentStep: 1 } as QualityInspectionWorkflow;
      }
      state.qualityInspectionWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityInspectionWorkflow['step1'];
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityWorkflowStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = { currentStep: 1 } as QualityInspectionWorkflow;
      }
      state.qualityInspectionWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityInspectionWorkflow['step2'];
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityWorkflowStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = { currentStep: 1 } as QualityInspectionWorkflow;
      }
      state.qualityInspectionWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityInspectionWorkflow['step3'];
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityWorkflowStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = { currentStep: 1 } as QualityInspectionWorkflow;
      }
      state.qualityInspectionWorkflow[modelKey].step4 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityInspectionWorkflow['step4'];
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityWorkflowStep5: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = { currentStep: 1 } as QualityInspectionWorkflow;
      }
      (state.qualityInspectionWorkflow[modelKey] as Record<string, unknown>).step5 = {
        ...result,
        timestamp: new Date().toISOString(),
      };
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityWorkflowCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.qualityInspectionWorkflow) {
        state.qualityInspectionWorkflow = {};
      }
      if (!state.qualityInspectionWorkflow[modelKey]) {
        state.qualityInspectionWorkflow[modelKey] = {} as QualityInspectionWorkflow;
      }
      state.qualityInspectionWorkflow[modelKey].currentStep = step;
      state.qualityInspectionWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearQualityInspectionWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.qualityInspectionWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.qualityInspectionWorkflow[modelKey];
      } else {
        state.qualityInspectionWorkflow = {};
      }
    },

    // ========================================
    // 품질조사 모델반영 3단계 워크플로우 액션들
    // ========================================

    initQualityApplyWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.qualityApplyWorkflow) {
        state.qualityApplyWorkflow = {};
      }
      state.qualityApplyWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        step3: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setQualityApplyStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityApplyWorkflow) {
        state.qualityApplyWorkflow = {};
      }
      if (!state.qualityApplyWorkflow[modelKey]) {
        state.qualityApplyWorkflow[modelKey] = { currentStep: 1 } as QualityApplyWorkflow;
      }
      state.qualityApplyWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityApplyWorkflow['step1'];
      state.qualityApplyWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityApplyStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityApplyWorkflow) {
        state.qualityApplyWorkflow = {};
      }
      if (!state.qualityApplyWorkflow[modelKey]) {
        state.qualityApplyWorkflow[modelKey] = { currentStep: 1 } as QualityApplyWorkflow;
      }
      state.qualityApplyWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityApplyWorkflow['step2'];
      state.qualityApplyWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityApplyStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.qualityApplyWorkflow) {
        state.qualityApplyWorkflow = {};
      }
      if (!state.qualityApplyWorkflow[modelKey]) {
        state.qualityApplyWorkflow[modelKey] = { currentStep: 1 } as QualityApplyWorkflow;
      }
      state.qualityApplyWorkflow[modelKey].step3 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as QualityApplyWorkflow['step3'];
      state.qualityApplyWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setQualityApplyCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.qualityApplyWorkflow) {
        state.qualityApplyWorkflow = {};
      }
      if (!state.qualityApplyWorkflow[modelKey]) {
        state.qualityApplyWorkflow[modelKey] = {} as QualityApplyWorkflow;
      }
      state.qualityApplyWorkflow[modelKey].currentStep = step;
      state.qualityApplyWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearQualityApplyWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.qualityApplyWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.qualityApplyWorkflow[modelKey];
      } else {
        state.qualityApplyWorkflow = {};
      }
    },

    // ========================================
    // 표준 분석 6단계 워크플로우 액션들
    // ========================================

    initStandardAnalysisWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      state.standardAnalysisWorkflow[modelKey] = {
        currentStep: 1,
        currentIteration: 1,
        iterations: [],
        accumulatedResults: {
          namingRules: { entityLogical: [], entityPhysical: [], attrLogical: [], attrPhysical: [] },
          refinementMethods: { active: [], excluded: [], llmSuggested: [] },
          termDictionary: [],
          wordDictionary: [],
          domainDictionary: [],
        },
        userRules: {
          customRefinementMethods: [],
          excludedRefinementMethods: [],
          acceptedSuggestions: [],
          rejectedSuggestions: [],
        },
        step1: undefined,
        step2: undefined,
        step3: undefined,
        step4: undefined,
        step5: undefined,
        step6: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setStandardAnalysisStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step1 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step1'];
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setStandardAnalysisStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step2 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step2'];
      // 누적 결과에 명명규칙 병합
      const namingRules = (result as { namingRules?: Record<string, unknown[]> }).namingRules;
      if (namingRules && state.standardAnalysisWorkflow[modelKey].accumulatedResults) {
        const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
          .namingRules as Record<string, unknown[]>;
        if (acc) {
          (['entityLogical', 'entityPhysical', 'attrLogical', 'attrPhysical'] as const).forEach(
            (cat) => {
              const newRules = (namingRules[cat] as unknown[]) || [];
              newRules.forEach((rule) => {
                const r = rule as { pattern?: string };
                if (!acc[cat]?.some((existing) => (existing as { pattern?: string }).pattern === r.pattern)) {
                  acc[cat]?.push(rule);
                }
              });
            }
          );
        }
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setStandardAnalysisStep3: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step3 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step3'];
      // LLM 제안 누적
      const llmSuggestions = (result as { llmSuggestions?: unknown[] }).llmSuggestions;
      if (llmSuggestions && state.standardAnalysisWorkflow[modelKey].accumulatedResults) {
        const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
          .refinementMethods as Record<string, unknown[]>;
        if (acc?.llmSuggested) {
          llmSuggestions.forEach((sug) => {
            const s = sug as { id?: string };
            if (!acc.llmSuggested.some((existing) => (existing as { id?: string }).id === s.id)) {
              acc.llmSuggested.push(sug);
            }
          });
        }
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    // Step 4: 단어사전 (워크플로우: 4.단어사전 → 5.용어사전)
    setStandardAnalysisStep4: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step4 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step4'];
      // 단어사전 누적 (유효한 단어만 - 논리명과 물리명이 모두 있는 경우)
      if (state.standardAnalysisWorkflow[modelKey].accumulatedResults) {
        const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
          .wordDictionary as unknown[];
        const resultData = result as {
          validWordCandidates?: unknown[];
          wordCandidates?: unknown[];
        };
        // validWordCandidates가 있으면 사용, 없으면 wordCandidates에서 필터링
        const wordsToAdd =
          resultData.validWordCandidates ||
          (resultData.wordCandidates || []).filter((w) => {
            const word = w as {
              physicalWord?: string;
              physicalName?: string;
              logicalWordKr?: string;
              logicalName?: string;
            };
            // 새 구조: physicalWord, logicalWordKr
            const physical = word.physicalWord || word.physicalName;
            const logicalKr = word.logicalWordKr || word.logicalName;
            return logicalKr?.trim() && physical?.trim();
          });
        wordsToAdd.forEach((word) => {
          const w = word as { physicalWord?: string; physicalName?: string };
          // 중복 검사: 물리명이 동일한 단어가 없으면 추가
          const physical = w.physicalWord || w.physicalName;
          if (
            !acc?.some((existing) => {
              const e = existing as { physicalWord?: string; physicalName?: string };
              return (e.physicalWord || e.physicalName) === physical;
            })
          ) {
            acc?.push(word);
          }
        });
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    // Step 5: 용어사전 (워크플로우: 4.단어사전 → 5.용어사전)
    setStandardAnalysisStep5: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step5 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step5'];
      // 용어사전 누적 (유효한 용어만 - 논리명과 물리명이 모두 있는 경우)
      if (state.standardAnalysisWorkflow[modelKey].accumulatedResults) {
        const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
          .termDictionary as unknown[];
        const resultData = result as {
          validTermCandidates?: unknown[];
          termCandidates?: unknown[];
        };
        // validTermCandidates가 있으면 사용, 없으면 termCandidates에서 필터링
        const termsToAdd =
          resultData.validTermCandidates ||
          (resultData.termCandidates || []).filter((t) => {
            const term = t as { physicalName?: string; logicalNameKr?: string; logicalName?: string };
            // 새 구조: physicalName, logicalNameKr
            const physical = term.physicalName;
            const logicalKr = term.logicalNameKr || term.logicalName;
            return logicalKr?.trim() && physical?.trim();
          });
        termsToAdd.forEach((term) => {
          const t = term as { physicalName?: string };
          // 중복 검사: 물리명이 동일한 용어가 없으면 추가
          if (!acc?.some((existing) => (existing as { physicalName?: string }).physicalName === t.physicalName)) {
            acc?.push(term);
          }
        });
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setStandardAnalysisStep6: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].step6 = {
        ...result,
        completed: true,
        timestamp: new Date().toISOString(),
      } as StandardAnalysisWorkflow['step6'];
      // 도메인사전 누적
      const domainCandidates = (result as { domainCandidates?: unknown[] }).domainCandidates;
      if (domainCandidates && state.standardAnalysisWorkflow[modelKey].accumulatedResults) {
        const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
          .domainDictionary as unknown[];
        domainCandidates.forEach((domain) => {
          // domainName > domainAbbr > name 순서로 확인 (하위 호환성)
          const d = domain as { domainName?: string; domainAbbr?: string; name?: string };
          const domainKey = d.domainName?.trim() || d.domainAbbr?.trim() || d.name?.trim();
          if (domainKey && !acc?.some((existing) => {
            const e = existing as { domainName?: string; domainAbbr?: string; name?: string };
            const existingKey = e.domainName?.trim() || e.domainAbbr?.trim() || e.name?.trim();
            return existingKey === domainKey;
          })) {
            // name 필드로 통일하여 저장 (d는 이미 객체로 캐스팅됨)
            acc?.push({ ...d, name: domainKey });
          }
        });
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setStandardAnalysisCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].currentStep = step;
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    startNewAnalysisIteration: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]) return;

      const workflow = state.standardAnalysisWorkflow[modelKey];
      // 현재 반복을 이력에 저장
      workflow.iterations.push({
        iterationId: String(workflow.currentIteration),
        step1: workflow.step1,
        step2: workflow.step2,
        step3: workflow.step3,
        step4: workflow.step4,
        step5: workflow.step5,
        step6: workflow.step6,
        timestamp: new Date().toISOString(),
      } as unknown as StandardAnalysisWorkflow['iterations'][0]);
      // 새 반복 시작
      workflow.currentIteration += 1;
      workflow.currentStep = 1;
      workflow.step1 = undefined;
      workflow.step2 = undefined;
      workflow.step3 = undefined;
      workflow.step4 = undefined;
      workflow.step5 = undefined;
      workflow.step6 = undefined;
      workflow.lastUpdated = new Date().toISOString();
    },

    // 사용자 정의 정제규칙 관리
    addCustomRefinementMethod: (state, action: PayloadAction<RefinementMethodPayload>) => {
      const { modelKey, method } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.userRules) return;
      state.standardAnalysisWorkflow[modelKey].userRules.customRefinementMethods?.push(method);
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    removeCustomRefinementMethod: (state, action: PayloadAction<RemoveRefinementMethodPayload>) => {
      const { modelKey, methodId } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.userRules) return;
      const methods = state.standardAnalysisWorkflow[modelKey].userRules.customRefinementMethods;
      if (methods) {
        const index = methods.findIndex((m) => (m as { id?: string }).id === methodId);
        if (index !== -1) {
          methods.splice(index, 1);
        }
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    excludeRefinementMethod: (state, action: PayloadAction<RemoveRefinementMethodPayload>) => {
      const { modelKey, methodId } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.userRules) return;
      const excluded = state.standardAnalysisWorkflow[modelKey].userRules.excludedRefinementMethods;
      if (excluded && !excluded.includes(methodId)) {
        excluded.push(methodId);
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    acceptLLMSuggestion: (state, action: PayloadAction<SuggestionPayload>) => {
      const { modelKey, suggestionId } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.userRules) return;
      const accepted = state.standardAnalysisWorkflow[modelKey].userRules.acceptedSuggestions;
      if (accepted && !accepted.includes(suggestionId)) {
        accepted.push(suggestionId);
      }
      // 제외 목록에서 제거
      const rejected = state.standardAnalysisWorkflow[modelKey].userRules.rejectedSuggestions;
      if (rejected) {
        const idx = rejected.indexOf(suggestionId);
        if (idx !== -1) {
          rejected.splice(idx, 1);
        }
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    rejectLLMSuggestion: (state, action: PayloadAction<SuggestionPayload>) => {
      const { modelKey, suggestionId } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.userRules) return;
      const rejected = state.standardAnalysisWorkflow[modelKey].userRules.rejectedSuggestions;
      if (rejected && !rejected.includes(suggestionId)) {
        rejected.push(suggestionId);
      }
      // 수락 목록에서 제거
      const accepted = state.standardAnalysisWorkflow[modelKey].userRules.acceptedSuggestions;
      if (accepted) {
        const idx = accepted.indexOf(suggestionId);
        if (idx !== -1) {
          accepted.splice(idx, 1);
        }
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearStandardAnalysisWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.standardAnalysisWorkflow) {
        return;
      }
      if (modelKey) {
        // 누적 사전은 유지하고 나머지만 초기화
        const existing = state.standardAnalysisWorkflow[modelKey];
        const preservedDictionaries = {
          termDictionary: existing?.accumulatedResults?.termDictionary || [],
          wordDictionary: existing?.accumulatedResults?.wordDictionary || [],
          domainDictionary: existing?.accumulatedResults?.domainDictionary || [],
        };

        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
          iterations: [],
          accumulatedResults: {
            namingRules: {
              entityLogical: [],
              entityPhysical: [],
              attrLogical: [],
              attrPhysical: [],
            },
            refinementMethods: { active: [], excluded: [], llmSuggested: [] },
            // 누적 사전 유지
            termDictionary: preservedDictionaries.termDictionary,
            wordDictionary: preservedDictionaries.wordDictionary,
            domainDictionary: preservedDictionaries.domainDictionary,
          },
          userRules: {
            customRefinementMethods: [],
            excludedRefinementMethods: [],
            acceptedSuggestions: [],
            rejectedSuggestions: [],
          },
          step1: undefined,
          step2: undefined,
          step3: undefined,
          step4: undefined,
          step5: undefined,
          step6: undefined,
          lastUpdated: new Date().toISOString(),
        };
      } else {
        // 전체 초기화 시에도 각 모델의 사전 유지
        Object.keys(state.standardAnalysisWorkflow).forEach((key) => {
          const existing = state.standardAnalysisWorkflow[key];
          const preservedDictionaries = {
            termDictionary: existing?.accumulatedResults?.termDictionary || [],
            wordDictionary: existing?.accumulatedResults?.wordDictionary || [],
            domainDictionary: existing?.accumulatedResults?.domainDictionary || [],
          };

          state.standardAnalysisWorkflow[key] = {
            currentStep: 1,
            currentIteration: 1,
            iterations: [],
            accumulatedResults: {
              namingRules: {
                entityLogical: [],
                entityPhysical: [],
                attrLogical: [],
                attrPhysical: [],
              },
              refinementMethods: { active: [], excluded: [], llmSuggested: [] },
              termDictionary: preservedDictionaries.termDictionary,
              wordDictionary: preservedDictionaries.wordDictionary,
              domainDictionary: preservedDictionaries.domainDictionary,
            },
            userRules: {
              customRefinementMethods: [],
              excludedRefinementMethods: [],
              acceptedSuggestions: [],
              rejectedSuggestions: [],
            },
            step1: undefined,
            step2: undefined,
            step3: undefined,
            step4: undefined,
            step5: undefined,
            step6: undefined,
            lastUpdated: new Date().toISOString(),
          };
        });
      }
    },

    /**
     * 특정 Step들만 초기화 (조사 범위 변경 시 사용)
     */
    resetStandardAnalysisSteps: (state, action: PayloadAction<ResetStepsPayload>) => {
      const { modelKey, resetSteps = [2, 3, 4, 5] } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]) return;

      resetSteps.forEach((stepNum) => {
        const stepKey = `step${stepNum}` as keyof StandardAnalysisWorkflow;
        (state.standardAnalysisWorkflow[modelKey] as Record<string, unknown>)[stepKey] = undefined;
      });
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 워크플로우 저장(persist) 설정
     * - true: 브라우저 새로고침 시 워크플로우 상태 복원
     * - false: 새로고침 시 초기화
     */
    setStandardAnalysisPersist: (state, action: PayloadAction<SetPersistWorkflowPayload>) => {
      const { modelKey, persist } = action.payload;
      if (!state.standardAnalysisWorkflow) {
        state.standardAnalysisWorkflow = {};
      }
      if (!state.standardAnalysisWorkflow[modelKey]) {
        state.standardAnalysisWorkflow[modelKey] = {
          currentStep: 1,
          currentIteration: 1,
        } as StandardAnalysisWorkflow;
      }
      state.standardAnalysisWorkflow[modelKey].persistWorkflow = persist;
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    // ========================================
    // 사전 등록 액션 (후보 → 누적 사전)
    // ========================================

    /**
     * 용어 후보를 누적 용어사전에 등록
     */
    registerTermsToDictionary: (state, action: PayloadAction<RegisterToDictionaryPayload>) => {
      const { modelKey, terms, registeredFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;
      if (!terms) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .termDictionary as unknown[];
      terms.forEach((term) => {
        // 신규 구조 및 기존 구조 호환
        const termLogical = term.logicalNameKr || term.logicalName;
        const termPhysical = term.physicalName;

        // 중복 검사: 물리명 기준 (물리명은 고유해야 함)
        const isDuplicate = acc?.some(
          (t) => (t as { physicalName?: string }).physicalName === termPhysical
        );

        if (!isDuplicate && termPhysical) {
          acc?.push({
            ...term,
            // 호환성을 위해 logicalName도 설정
            logicalName: termLogical,
            registeredAt: new Date().toISOString(),
            registeredFrom: registeredFrom || '표준분석',
          });
        }
      });
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 단어 후보를 누적 단어사전에 등록
     */
    registerWordsToDictionary: (state, action: PayloadAction<RegisterToDictionaryPayload>) => {
      const { modelKey, words, registeredFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;
      if (!words) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .wordDictionary as unknown[];
      words.forEach((word) => {
        // 신규 구조 및 기존 구조 호환
        const wordLogical = word.logicalWordKr || word.logicalName;
        const wordPhysical = word.physicalWord || word.physicalName;

        // 중복 검사: 물리명 기준 (물리명은 고유해야 함)
        const isDuplicate = acc?.some((w) => {
          const existing = w as { physicalWord?: string; physicalName?: string };
          const existingPhysical = existing.physicalWord || existing.physicalName;
          return existingPhysical === wordPhysical;
        });

        if (!isDuplicate && wordPhysical) {
          acc?.push({
            ...word,
            // 호환성을 위해 logicalName, physicalName도 설정
            logicalName: wordLogical,
            physicalName: wordPhysical,
            registeredAt: new Date().toISOString(),
            registeredFrom: registeredFrom || '표준분석',
          });
        }
      });
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 도메인 후보를 누적 도메인사전에 등록
     */
    registerDomainsToDictionary: (state, action: PayloadAction<RegisterToDictionaryPayload>) => {
      const { modelKey, domains, registeredFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;
      if (!domains) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .domainDictionary as unknown[];
      domains.forEach((domain) => {
        // 도메인 식별자: domainName > domainAbbr > name 순서로 확인 (하위 호환성)
        const domainKey = domain.domainName?.trim() || domain.domainAbbr?.trim() || domain.name?.trim();

        // 중복 검사: 도메인명(name) 기준
        const isDuplicate = acc?.some((d) => (d as { name?: string }).name?.trim() === domainKey);

        if (!isDuplicate && domainKey) {
          // domainAbbr 필드는 저장하지 않고 name으로 통일
          const { domainAbbr, ...domainWithoutAbbr } = domain;
          acc?.push({
            ...domainWithoutAbbr,
            name: domainKey,
            registeredAt: new Date().toISOString(),
            registeredFrom: registeredFrom || '표준분석',
          });
        }
      });
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 누적 사전에서 항목 제거
     */
    removeFromDictionary: (state, action: PayloadAction<RemoveFromDictionaryPayload>) => {
      const { modelKey, dictionaryType, itemId, physicalName, name } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const accResults = state.standardAnalysisWorkflow[modelKey].accumulatedResults;

      if (dictionaryType === 'term') {
        // 물리명 기준으로 검색 (물리명은 고유)
        const arr = accResults.termDictionary as unknown[];
        const idx = arr?.findIndex(
          (t) =>
            (t as { id?: string }).id === itemId ||
            (t as { physicalName?: string }).physicalName === physicalName
        );
        if (idx !== undefined && idx !== -1) arr?.splice(idx, 1);
      } else if (dictionaryType === 'word') {
        // 신규 구조 및 기존 구조 호환 - 물리명 기준으로 검색
        const arr = accResults.wordDictionary as unknown[];
        const idx = arr?.findIndex(
          (w) =>
            (w as { id?: string }).id === itemId ||
            ((w as { physicalWord?: string; physicalName?: string }).physicalWord ||
              (w as { physicalWord?: string; physicalName?: string }).physicalName) === physicalName
        );
        if (idx !== undefined && idx !== -1) arr?.splice(idx, 1);
      } else if (dictionaryType === 'domain') {
        // name 기준으로 검색
        const arr = accResults.domainDictionary as unknown[];
        const idx = arr?.findIndex(
          (d) =>
            (d as { id?: string }).id === itemId || (d as { name?: string }).name === name
        );
        if (idx !== undefined && idx !== -1) arr?.splice(idx, 1);
      }

      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 용어 사전 항목 수정
     */
    updateTermInDictionary: (state, action: PayloadAction<UpdateDictionaryItemPayload>) => {
      const { modelKey, originalPhysicalName, updatedTerm, updatedFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .termDictionary as unknown[];
      const idx = acc?.findIndex(
        (t) => (t as { physicalName?: string }).physicalName === originalPhysicalName
      );
      if (idx !== undefined && idx !== -1 && acc) {
        acc[idx] = {
          ...(acc[idx] as Record<string, unknown>),
          ...updatedTerm,
          updatedAt: new Date().toISOString(),
          updatedFrom: updatedFrom || '표준분석',
        };
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 단어 사전 항목 수정
     */
    updateWordInDictionary: (state, action: PayloadAction<UpdateDictionaryItemPayload>) => {
      const { modelKey, originalPhysicalName, updatedWord, updatedFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .wordDictionary as unknown[];
      const idx = acc?.findIndex((w) => {
        const word = w as { physicalWord?: string; physicalName?: string };
        return (word.physicalWord || word.physicalName) === originalPhysicalName;
      });
      if (idx !== undefined && idx !== -1 && acc) {
        acc[idx] = {
          ...(acc[idx] as Record<string, unknown>),
          ...updatedWord,
          updatedAt: new Date().toISOString(),
          updatedFrom: updatedFrom || '표준분석',
        };
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    /**
     * 도메인 사전 항목 수정
     */
    updateDomainInDictionary: (state, action: PayloadAction<UpdateDictionaryItemPayload>) => {
      const { modelKey, originalName, updatedDomain, updatedFrom } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults
        .domainDictionary as unknown[];
      // name 기준으로 검색
      const idx = acc?.findIndex((d) => (d as { name?: string }).name === originalName);
      if (idx !== undefined && idx !== -1 && acc) {
        const existing = acc[idx] as { name?: string };
        acc[idx] = {
          ...(acc[idx] as Record<string, unknown>),
          ...updatedDomain,
          name: (updatedDomain as { name?: string })?.name || existing.name,
          updatedAt: new Date().toISOString(),
          updatedFrom: updatedFrom || '표준분석',
        };
      }
      state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    // ========================================
    // 기존 사전 데이터 마이그레이션 (등록일시/등록경로 필드 추가)
    // ========================================

    /**
     * 기존 사전 데이터에 registeredAt/registeredFrom 필드가 없으면 추가
     */
    migrateDictionaryFields: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults;
      const migrationTime = new Date().toISOString();

      // 용어사전 마이그레이션
      (acc.termDictionary as Array<{ registeredAt?: string; registeredFrom?: string }>)?.forEach(
        (term) => {
          if (!term.registeredAt) {
            term.registeredAt = migrationTime;
            term.registeredFrom = '표준분석(마이그레이션)';
          }
        }
      );

      // 단어사전 마이그레이션
      (acc.wordDictionary as Array<{ registeredAt?: string; registeredFrom?: string }>)?.forEach(
        (word) => {
          if (!word.registeredAt) {
            word.registeredAt = migrationTime;
            word.registeredFrom = '표준분석(마이그레이션)';
          }
        }
      );

      // 도메인사전 마이그레이션
      (acc.domainDictionary as Array<{ registeredAt?: string; registeredFrom?: string }>)?.forEach(
        (domain) => {
          if (!domain.registeredAt) {
            domain.registeredAt = migrationTime;
            domain.registeredFrom = '표준분석(마이그레이션)';
          }
        }
      );

      state.standardAnalysisWorkflow[modelKey].lastUpdated = migrationTime;
    },

    /**
     * 사전에서 유효하지 않은 항목(null/빈 키값) 제거
     */
    cleanupInvalidDictionaryItems: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.standardAnalysisWorkflow?.[modelKey]?.accumulatedResults) return;

      const acc = state.standardAnalysisWorkflow[modelKey].accumulatedResults;
      let removedCount = 0;

      // 용어사전: 물리명이 없는 항목 제거
      const termsBefore = acc.termDictionary?.length || 0;
      acc.termDictionary = (acc.termDictionary || []).filter(
        (t) => t.physicalName?.trim()
      );
      removedCount += termsBefore - (acc.termDictionary?.length || 0);

      // 단어사전: 물리명이 없는 항목 제거
      const wordsBefore = acc.wordDictionary?.length || 0;
      acc.wordDictionary = (acc.wordDictionary || []).filter((w) => {
        return (w.physicalWord || w.physicalName)?.trim();
      });
      removedCount += wordsBefore - (acc.wordDictionary?.length || 0);

      // 도메인사전: 도메인명이 없는 항목 제거
      const domainsBefore = acc.domainDictionary?.length || 0;
      acc.domainDictionary = (acc.domainDictionary || []).filter(
        (d) => (d.domainName || d.name)?.trim()
      );
      removedCount += domainsBefore - (acc.domainDictionary?.length || 0);

      if (removedCount > 0) {
        state.standardAnalysisWorkflow[modelKey].lastUpdated = new Date().toISOString();
      }
    },

    // ========================================
    // 속성 일괄 작업 2단계 워크플로우 액션들
    // ========================================

    initAttributeBatchWorkflow: (state, action: PayloadAction<ModelKeyPayload>) => {
      const { modelKey } = action.payload;
      if (!state.attributeBatchWorkflow) {
        state.attributeBatchWorkflow = {};
      }
      // 기존 데이터가 있으면 유지 (persist 복원된 데이터 보호)
      const existing = state.attributeBatchWorkflow[modelKey];
      if (existing && (existing.step1 || existing.step2)) {
        // 이미 데이터가 있으면 currentStep만 유지하고 lastUpdated만 갱신
        return;
      }
      state.attributeBatchWorkflow[modelKey] = {
        currentStep: 1,
        step1: undefined,
        step2: undefined,
        lastUpdated: new Date().toISOString(),
      };
    },

    setAttributeBatchStep1: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeBatchWorkflow) {
        state.attributeBatchWorkflow = {};
      }
      if (!state.attributeBatchWorkflow[modelKey]) {
        state.attributeBatchWorkflow[modelKey] = { currentStep: 1 } as AttributeBatchWorkflow;
      }
      state.attributeBatchWorkflow[modelKey].step1 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as AttributeBatchWorkflow['step1'];
      state.attributeBatchWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeBatchStep2: (state, action: PayloadAction<SetWorkflowStepPayload>) => {
      const { modelKey, result } = action.payload;
      if (!state.attributeBatchWorkflow) {
        state.attributeBatchWorkflow = {};
      }
      if (!state.attributeBatchWorkflow[modelKey]) {
        state.attributeBatchWorkflow[modelKey] = { currentStep: 1 } as AttributeBatchWorkflow;
      }
      state.attributeBatchWorkflow[modelKey].step2 = {
        ...result,
        timestamp: new Date().toISOString(),
      } as AttributeBatchWorkflow['step2'];
      state.attributeBatchWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    setAttributeBatchCurrentStep: (state, action: PayloadAction<SetCurrentStepPayload>) => {
      const { modelKey, step } = action.payload;
      if (!state.attributeBatchWorkflow) {
        state.attributeBatchWorkflow = {};
      }
      if (!state.attributeBatchWorkflow[modelKey]) {
        state.attributeBatchWorkflow[modelKey] = { currentStep: 1 } as AttributeBatchWorkflow;
      }
      state.attributeBatchWorkflow[modelKey].currentStep = step;
      state.attributeBatchWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    clearAttributeBatchWorkflow: (state, action: PayloadAction<string | undefined>) => {
      const modelKey = action.payload;
      if (!state.attributeBatchWorkflow) {
        return;
      }
      if (modelKey) {
        delete state.attributeBatchWorkflow[modelKey];
      } else {
        state.attributeBatchWorkflow = {};
      }
    },

    /**
     * 속성 일괄 작업 워크플로우 저장(persist) 설정
     * - true: 브라우저 새로고침 시 워크플로우 상태 복원
     * - false: 새로고침 시 초기화
     */
    setAttributeBatchPersist: (state, action: PayloadAction<SetPersistWorkflowPayload>) => {
      const { modelKey, persist } = action.payload;
      if (!state.attributeBatchWorkflow) {
        state.attributeBatchWorkflow = {};
      }
      if (!state.attributeBatchWorkflow[modelKey]) {
        state.attributeBatchWorkflow[modelKey] = { currentStep: 1 } as AttributeBatchWorkflow;
      }
      state.attributeBatchWorkflow[modelKey].persistWorkflow = persist;
      state.attributeBatchWorkflow[modelKey].lastUpdated = new Date().toISOString();
    },

    // ========================================
    // DA Extension 전체 상태 초기화
    // ========================================

    resetDAExtensionState: () => {
      return initialState;
    },
  },
});

export const {
  // 표준조사 결과 관련 액션
  setNamingRuleResult,
  setWordCandidateResult,
  setTermCandidateResult,
  setDomainCandidateResult,
  setProjectTermCandidateResult,
  clearProjectTermCandidateResult,

  // 용어 후보 조사 5단계 워크플로우 액션
  initTermCandidateWorkflow,
  setTermWorkflowStep1,
  setTermWorkflowStep2,
  setTermWorkflowStep3,
  setTermWorkflowStep4,
  setTermWorkflowStep5,
  setTermWorkflowCurrentStep,
  clearTermCandidateWorkflow,

  // 단어 후보 조사 4단계 워크플로우 액션
  initWordCandidateWorkflow,
  setWordWorkflowStep1,
  setWordWorkflowStep2,
  setWordWorkflowStep3,
  setWordWorkflowStep4,
  setWordWorkflowCurrentStep,
  clearWordCandidateWorkflow,

  // 명명규칙 조사 3단계 워크플로우 액션
  initNamingRuleWorkflow,
  setNamingRuleWorkflowStep1,
  setNamingRuleWorkflowStep2,
  setNamingRuleWorkflowStep3,
  setNamingRuleWorkflowCurrentStep,
  clearNamingRuleWorkflow,

  // 모델정제 관련 액션
  setEntityRefineResult,
  setAttributeRefineResult,
  clearEntityRefineResult,
  clearAttributeRefineResult,

  // 엔터티명 정제 워크플로우 액션
  initEntityRefineWorkflow,
  setEntityRefineStep1,
  setEntityRefineStep2,
  setEntityRefineStep3,
  setEntityRefineStep4,
  setEntityRefineCurrentStep,
  setEntityRefineLLMSettings,
  clearEntityRefineWorkflow,

  // 속성명 정제 워크플로우 액션
  initAttributeRefineWorkflow,
  setAttributeRefineStep1,
  setAttributeRefineStep2,
  setAttributeRefineStep3,
  setAttributeRefineStep4,
  setAttributeRefineCurrentStep,
  setAttributeRefineLLMSettings,
  clearAttributeRefineWorkflow,

  // 품질조사 5단계 워크플로우 액션
  initQualityInspectionWorkflow,
  setQualityWorkflowStep1,
  setQualityWorkflowStep2,
  setQualityWorkflowStep3,
  setQualityWorkflowStep4,
  setQualityWorkflowStep5,
  setQualityWorkflowCurrentStep,
  clearQualityInspectionWorkflow,

  // 품질조사 모델반영 3단계 워크플로우 액션
  initQualityApplyWorkflow,
  setQualityApplyStep1,
  setQualityApplyStep2,
  setQualityApplyStep3,
  setQualityApplyCurrentStep,
  clearQualityApplyWorkflow,

  // 표준 분석 6단계 워크플로우 액션
  initStandardAnalysisWorkflow,
  setStandardAnalysisStep1,
  setStandardAnalysisStep2,
  setStandardAnalysisStep3,
  setStandardAnalysisStep4,
  setStandardAnalysisStep5,
  setStandardAnalysisStep6,
  setStandardAnalysisCurrentStep,
  startNewAnalysisIteration,
  addCustomRefinementMethod,
  removeCustomRefinementMethod,
  excludeRefinementMethod,
  acceptLLMSuggestion,
  rejectLLMSuggestion,
  clearStandardAnalysisWorkflow,
  resetStandardAnalysisSteps,
  setStandardAnalysisPersist,

  // 사전 등록 액션
  registerTermsToDictionary,
  registerWordsToDictionary,
  registerDomainsToDictionary,
  removeFromDictionary,

  // 사전 수정 액션
  updateTermInDictionary,
  updateWordInDictionary,
  updateDomainInDictionary,

  // 사전 데이터 마이그레이션
  migrateDictionaryFields,

  // 사전 정리
  cleanupInvalidDictionaryItems,

  // 속성 일괄 작업 워크플로우 액션
  initAttributeBatchWorkflow,
  setAttributeBatchStep1,
  setAttributeBatchStep2,
  setAttributeBatchCurrentStep,
  clearAttributeBatchWorkflow,
  setAttributeBatchPersist,

  // 전체 초기화
  resetDAExtensionState,
} = daExtensionSlice.actions;

export default daExtensionSlice.reducer;
